import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type {
  TranslationProviderProps,
  TranslationResources,
  TranslationLookup,
  TranslationArg,
  Language,
} from '../types';
import { interpolate, resolveTranslation } from './interpolate';
import { DefaultTranslationEngine } from '../engines/DefaultTranslationEngine';
import { languageStorage } from '../storage/languageStorage';

/**
 * Interfaz del contexto interno de traducción
 */
interface TranslationContextType {
  /**
   * Traduce una llave y, si se pasan `params`, sustituye sus marcadores `{{nombre}}`.
   *
   * <para>`params` es OPCIONAL y el parámetro se añadió sin cambiar la forma de un solo argumento,
   * así que todas las llamadas existentes siguen valiendo. Antes de esto el kit no tenía forma de
   * interpolar: una clave del catálogo con marcadores llegaba a la pantalla con el marcador crudo
   * («Detalle — {{number}}»), y cada consumidor lo rodeaba con `t(...).replace('{{x}}', v)` — un
   * `replace` por marcador, que es exactamente donde se olvida el segundo.</para>
   */
  translate: (key: string, arg?: TranslationArg) => string;
  t: (key: string, arg?: TranslationArg) => string; // Alias común
  /**
   * Resolución con procedencia: devuelve el valor y el idioma en el que se
   * encontró, o `null` si la clave no está registrada. Permite a un consumidor
   * con diccionarios propios distinguir una traducción del idioma activo de un
   * fallback al idioma base. Ver `TranslationEngine.resolve`.
   *
   * Es opcional: un motor de terceros puede no implementarlo.
   */
  resolve?: (key: string) => TranslationLookup | null;
  setLanguage: (lang: string) => Promise<void>;
  currentLanguage: string;
  languages: Language[];
  isReady: boolean;
  error: string | null;
  /**
   * Invalidación explícita: borra la caché del idioma activo (y del idioma por
   * defecto, que se usa como fallback) y vuelve a pedir el catálogo al backend.
   */
  refreshTranslations: () => Promise<void>;
  /**
   * Contador que se incrementa cada vez que el motor recibe recursos nuevos.
   *
   * El motor de traducción es un objeto mutable, así que cargarle recursos NO
   * re-renderiza a los consumidores. Este contador viaja en el `value` del
   * contexto para invalidarlos cuando llega un catálogo fresco.
   */
  resourceVersion: number;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

/**
 * Claves en las que un host puede envolver el código de idioma dentro del `detail` del broadcast.
 * El contrato tipado es un string plano (`CustomEvent<string>`), pero los shells reales difunden
 * `{ language: 'en-US' }`, así que también se aceptan las envolturas equivalentes.
 */
const LANGUAGE_CODE_KEYS = ['language', 'lang', 'codigo', 'code'] as const;

/**
 * Extrae el código de idioma de un valor que NO controla el kit.
 *
 * El `detail` del evento `broadcastEventName` lo produce el host (el Shell), no el provider, y hasta
 * ahora entraba VERBATIM al estado: un objeto ahí se propagaba a `currentLanguage`, al motor de
 * traducción, al path de `apiAdapter.getTranslations()` y a la cabecera `Accept-Language`. Del lado
 * del consumidor eso reventaba cualquier `currentLanguage.toLowerCase()` — el tipo promete `string` —
 * y con ello el render completo del microfrontend.
 *
 * Ley de Postel: se acepta tanto el string del contrato como la envoltura que difunden los shells, y
 * cualquier otra cosa devuelve `null` para que el llamador la descarte sin tocar el estado.
 */
const readLanguageCode = (value: unknown): string | null => {
  if (typeof value === 'string') return value !== '' ? value : null;

  if (value !== null && typeof value === 'object') {
    for (const key of LANGUAGE_CODE_KEYS) {
      const candidate = (value as Record<string, unknown>)[key];
      if (typeof candidate === 'string' && candidate !== '') return candidate;
    }
  }

  return null;
};

/**
 * Provider principal para la internacionalización de Siesa
 *
 * ## Política de caché: stale-while-revalidate
 *
 * El catálogo cacheado en `localStorage` se sirve al instante (para no bloquear
 * el primer render) y *además* se revalida contra el backend. Cuando llegan los
 * datos frescos se mergean en el motor, se reescribe la caché y se re-renderiza
 * a los consumidores. Así las claves nuevas añadidas en el backend aparecen en
 * la siguiente carga de la app sin limpiar la caché a mano.
 *
 * La revalidación automática ocurre una vez por idioma y carga de la app.
 * Para forzarla en caliente existe `refreshTranslations()`, y un evento de
 * cambio de sesión (`sessionChangeEventName`) limpia la caché completa.
 *
 * En modo `mfe` se mantiene la optimización de red: si el microfrontend ya tiene
 * catálogo (caché o memoria) no llama al API; solo el Shell revalida y difunde
 * el resultado por `broadcastEventName`.
 */
export const TranslationProvider: React.FC<TranslationProviderProps> = ({
  children,
  mode = 'standalone',
  broadcastEventName = 'siesa:language-changed',
  sessionChangeEventName = 'siesa:session-changed',
  apiAdapter,
  staticLanguages,
  staticResources,
  defaultLanguage = 'es',
  autoSetAxiosHeader = false,
  axiosInstance,
}) => {
  const [currentLanguage, setCurrentLanguage] = useState<string>(
    () => languageStorage.getLanguage() || defaultLanguage
  );
  const [languages, setLanguages] = useState<Language[]>(staticLanguages || []);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Ver `resourceVersion` en TranslationContextType: fuerza el re-render de los
  // consumidores cuando el motor (objeto mutable) recibe recursos nuevos.
  const [resourceVersion, setResourceVersion] = useState(0);

  // Instanciamos el motor de traducción (una sola vez)
  const engine = useMemo(() => {
    const instance = new DefaultTranslationEngine(defaultLanguage);
    // Fijamos el idioma activo ANTES del primer render: la caché se sirve de
    // inmediato y debe resolverse en el idioma persistido, no en el default.
    instance.changeLanguage(languageStorage.getLanguage() || defaultLanguage);
    return instance;
  }, [defaultLanguage]);

  // Peticiones de revalidación en vuelo, por idioma. Evita que dos llamadas
  // concurrentes para el mismo idioma disparen dos fetches.
  const inFlightRef = useRef<Map<string, Promise<void>>>(new Map());

  // Idiomas ya revalidados en esta carga de la app. Evita que un re-render con
  // `apiAdapter` inline (identidad nueva en cada render) provoque una tormenta
  // de peticiones. La invalidación explícita no pasa por este control.
  const revalidatedRef = useRef<Set<string>>(new Set());

  // Idioma vigente legible desde código asíncrono. `currentLanguage` no sirve para
  // eso: quien lo lea dentro de una función `async` ve el valor congelado en el
  // closure del render que la creó, y aplicarlo al terminar revierte cualquier
  // `setLanguage` que haya ocurrido mientras tanto. Es exactamente lo que pasaba
  // al arrancar: los efectos de los hijos corren antes que los del padre, así que
  // un hijo que pedía otro idioma al montarse perdía siempre la carrera contra el
  // efecto de inicialización, y el motor quedaba en un idioma distinto al del
  // estado — textos viejos con fechas y números del idioma nuevo.
  const idiomaVigenteRef = useRef(currentLanguage);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Actualizar header de axios si es necesario
  const updateAxiosHeader = useCallback(
    (lang: string) => {
      if (autoSetAxiosHeader && axiosInstance) {
        if (axiosInstance.defaults && axiosInstance.defaults.headers) {
          axiosInstance.defaults.headers.common['Accept-Language'] = lang;
        }
      }
    },
    [autoSetAxiosHeader, axiosInstance]
  );

  /**
   * Registra recursos en el motor y notifica a los consumidores.
   */
  const applyResources = useCallback(
    (lang: string, resources: TranslationResources | null | undefined) => {
      if (!resources || Object.keys(resources).length === 0) return;

      engine.loadResources(lang, resources);
      if (isMountedRef.current) {
        setResourceVersion((v) => v + 1);
      }
    },
    [engine]
  );

  /**
   * Pide el catálogo al backend y lo mergea en el motor.
   *
   * Deduplica por idioma: si ya hay una petición en vuelo se reutiliza su promesa.
   * Propaga el error al llamador, que decide si es fatal (no había nada que pintar)
   * o si basta con avisar por consola (ya se sirvió caché).
   */
  const revalidateResources = useCallback(
    (lang: string): Promise<void> => {
      if (!apiAdapter) return Promise.resolve();

      const inFlight = inFlightRef.current;
      const pending = inFlight.get(lang);
      if (pending) return pending;

      const request = (async () => {
        try {
          const fresh = await apiAdapter.getTranslations(lang);
          applyResources(lang, fresh);
          // Solo el Shell o un Standalone escriben la caché compartida
          // (evita sobre-escrituras cruzadas entre MFEs).
          if (mode !== 'mfe' && fresh && Object.keys(fresh).length > 0) {
            languageStorage.setTranslations(lang, fresh);
          }
        } finally {
          inFlight.delete(lang);
        }
      })();

      inFlight.set(lang, request);
      return request;
    },
    [apiAdapter, applyResources, mode]
  );

  /**
   * Carga los recursos de un idioma: estáticos, o caché + revalidación.
   */
  const loadLanguageResources = useCallback(
    async (lang: string) => {
      // 1. Intentar desde staticResources (Modo Desconectado): no hay red que revalidar
      if (staticResources && staticResources[lang]) {
        applyResources(lang, staticResources[lang]);
        return;
      }

      // 2. Servir la caché al instante (stale) para no bloquear el primer render
      const cached = languageStorage.getTranslations(lang);
      if (cached) {
        applyResources(lang, cached);
      }

      if (!apiAdapter) return;

      const hasSomethingToRender = Boolean(cached) || engine.hasResources(lang);

      // 3. OPTIMIZACIÓN MFE: si somos un microfrontend y ya tenemos catálogo,
      // no tocamos la red. Solo el Shell revalida y difunde por `broadcastEventName`.
      if (mode === 'mfe' && hasSomethingToRender) return;

      // 4. Revalidación automática: una vez por idioma y carga de la app.
      if (revalidatedRef.current.has(lang) && hasSomethingToRender) return;
      revalidatedRef.current.add(lang);

      if (hasSomethingToRender) {
        // Ya hay algo que pintar: revalidamos en segundo plano sin bloquear el render.
        void revalidateResources(lang).catch((err) => {
          console.warn(
            `[i18n] No se pudo revalidar el catálogo de [${lang}]. Se mantiene la versión en caché.`,
            err
          );
        });
        return;
      }

      // 5. Sin nada que pintar: hay que esperar la respuesta del backend.
      try {
        await revalidateResources(lang);
      } catch (err) {
        console.error(`Error cargando recursos de idioma [${lang}]:`, err);
        if (isMountedRef.current) {
          setError(`No se pudieron cargar las traducciones para el idioma: ${lang}`);
        }
      }
    },
    [staticResources, applyResources, apiAdapter, engine, mode, revalidateResources]
  );

  /**
   * Carga la lista de idiomas disponibles
   */
  const loadAvailableLanguages = useCallback(async () => {
    try {
      // 1. Si ya tenemos idiomas estáticos, usarlos directamente
      if (staticLanguages && staticLanguages.length > 0) {
        setLanguages(staticLanguages);
        return;
      }

      // 2. Intentar cargar desde cache para disponibilidad inmediata
      const availableLangs = languageStorage.getAvailableLanguages();
      if (availableLangs) {
        setLanguages(availableLangs);
      }

      // 3. Refrescar desde el API si hay adaptador
      // OPTIMIZACIÓN MFE: Si somos un microfrontend y ya tenemos caché, omitimos la llamada a la API
      // para que solo el Shell sea el que interactúe con la red
      if (apiAdapter && !(mode === 'mfe' && availableLangs)) {
        const freshLangs = await apiAdapter.getLanguages();
        if (freshLangs && JSON.stringify(freshLangs) !== JSON.stringify(availableLangs)) {
          if (isMountedRef.current) {
            setLanguages(freshLangs);
          }
          if (mode !== 'mfe') {
            languageStorage.setAvailableLanguages(freshLangs);
          }
        }
      }
    } catch (err) {
      console.error('Error cargando idiomas disponibles:', err);
    }
  }, [staticLanguages, apiAdapter, mode]);

  /**
   * Cambia el idioma activo de la aplicación
   */
  const setLanguage = useCallback(
    async (langInput: string, fromBroadcast = false) => {
      // Si somos MFE y el cambio no viene del broadcast, ignoramos o avisamos.
      // Solo el Shell o un Standalone deberían cambiar el idioma por acción del usuario directamente
      // Pero permitimos el cambio si es forzado por el broadcast global
      if (mode === 'mfe' && !fromBroadcast) {
        console.warn('Los MFEs no deberían cambiar el idioma manualmente. Use el Shell para cambiar el idioma global.');
        return;
      }

      // ÚNICO punto de escritura del idioma: aquí se normaliza para que nada que no sea un código
      // string llegue al estado, al motor, al path del catálogo ni a `Accept-Language`. Vale tanto
      // para el broadcast del shell como para un consumidor que llame `setLanguage` con un objeto.
      const lang = readLanguageCode(langInput);
      if (lang === null) {
        console.warn(
          '[i18n] setLanguage recibió un idioma que no es un código válido; se ignora el cambio.',
          langInput
        );
        return;
      }

      // Se marca el idioma vigente ANTES de esperar por los recursos: desde este
      // punto es la intención en firme, y cualquier trabajo asíncrono que aún esté
      // en vuelo (el arranque, sobre todo) debe respetarla en vez de reimponer el
      // idioma con el que empezó.
      idiomaVigenteRef.current = lang;

      setIsReady(false);

      // 1. Asegurar recursos cargados (sirve caché y revalida en segundo plano)
      await loadLanguageResources(lang);

      // 2. Actualizar estado y motor
      setCurrentLanguage(lang);
      engine.changeLanguage(lang);

      if (mode !== 'mfe') {
        languageStorage.setLanguage(lang);
      }

      updateAxiosHeader(lang);

      setIsReady(true);

      // 3. BROADCAST (Si somos Shell, avisar al ecosistema)
      if (mode === 'shell' && !fromBroadcast) {
        const event = new CustomEvent(broadcastEventName, { detail: lang });
        window.dispatchEvent(event);
      }
    },
    [mode, loadLanguageResources, engine, updateAxiosHeader, broadcastEventName]
  );

  /**
   * Invalidación explícita del catálogo.
   *
   * Borra la caché del idioma activo (y del default, que alimenta los fallbacks)
   * y vuelve a pedirlos al backend. No pasa por el control de "una revalidación
   * por carga": si el llamador la invoca, quiere datos frescos.
   */
  const refreshTranslations = useCallback(async () => {
    const targets =
      currentLanguage === defaultLanguage ? [currentLanguage] : [currentLanguage, defaultLanguage];

    // Los MFEs no escriben ni borran la caché compartida: la gestiona el Shell.
    if (mode !== 'mfe') {
      targets.forEach((lang) => languageStorage.removeTranslations(lang));
    }

    // Modo desconectado (staticResources / sin adaptador): no hay backend que consultar.
    if (!apiAdapter) return;

    const results = await Promise.allSettled(targets.map((lang) => revalidateResources(lang)));
    const failed = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failed) {
      console.warn('[i18n] refreshTranslations: no se pudo refrescar el catálogo.', failed.reason);
    }
  }, [currentLanguage, defaultLanguage, mode, apiAdapter, revalidateResources]);

  /**
   * Inicialización del sistema
   */
  useEffect(() => {
    const init = async () => {
      // 1. Cargar lista de idiomas en paralelo
      loadAvailableLanguages();

      // 2. Cargar recursos del idioma actual. Se lee de la ref y no de
      // `currentLanguage`: un hijo que haya pedido otro idioma al montarse ya lo
      // dejó anotado ahí, y cargar el del closure traería el catálogo equivocado.
      await loadLanguageResources(idiomaVigenteRef.current);

      // 3. Si el actual no es el default, precargar el default para fallbacks
      if (idiomaVigenteRef.current !== defaultLanguage) {
        await loadLanguageResources(defaultLanguage);
      }

      // 4. Sincronizar motor y header con el idioma vigente AHORA, no con el que
      // había cuando arrancó este efecto: los dos `await` de arriba dan margen a
      // que `setLanguage` cambie el idioma mientras estaban en vuelo. Si el
      // arranque llega tarde, esto es un no-op idempotente; si llega primero, deja
      // el motor donde el estado de React ya lo espera.
      const vigente = idiomaVigenteRef.current;
      engine.changeLanguage(vigente);
      updateAxiosHeader(vigente);

      if (isMountedRef.current) {
        setIsReady(true);
      }
    };

    init();
    // La inicialización debe correr una vez por adaptador/modo, NO cuando cambian
    // las identidades de los callbacks (un `apiAdapter` o `staticResources` inline
    // cambia de identidad en cada render y re-lanzaría el arranque en bucle).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiAdapter, defaultLanguage, mode]);

  /**
   * Suscripción a eventos de Microfrontends (Modo MFE)
   */
  useEffect(() => {
    if (mode !== 'mfe') return;

    const handleLanguageChange = (e: Event) => {
      // El `detail` es del host: se normaliza antes de compararlo o propagarlo (ver readLanguageCode).
      const customEvent = e as CustomEvent<unknown>;
      const newLang = readLanguageCode(customEvent.detail);

      if (newLang !== null && newLang !== currentLanguage) {
        // Forzar cambio viniendo del evento global
        setLanguage(newLang, true);
      }
    };

    window.addEventListener(broadcastEventName, handleLanguageChange);

    return () => {
      window.removeEventListener(broadcastEventName, handleLanguageChange);
    };
  }, [mode, broadcastEventName, currentLanguage, setLanguage]);

  /**
   * Cambio de sesión (login / logout): el catálogo cacheado pertenece al usuario
   * anterior, así que se limpia la caché y se revalida contra el backend.
   */
  useEffect(() => {
    if (!sessionChangeEventName) return;

    const handleSessionChange = () => {
      languageStorage.clearCache();
      revalidatedRef.current.clear();
      loadAvailableLanguages();
      void refreshTranslations();
    };

    window.addEventListener(sessionChangeEventName, handleSessionChange);

    return () => {
      window.removeEventListener(sessionChangeEventName, handleSessionChange);
    };
  }, [sessionChangeEventName, refreshTranslations, loadAvailableLanguages]);

  const value = useMemo<TranslationContextType>(
    () => ({
      translate: (key: string, arg?: TranslationArg) =>
        resolveTranslation(key, engine.translate(key), arg),
      t: (key: string, arg?: TranslationArg) => resolveTranslation(key, engine.translate(key), arg),
      resolve: (key: string) => engine.resolve(key),
      setLanguage,
      currentLanguage,
      languages,
      isReady,
      error,
      refreshTranslations,
      resourceVersion,
    }),
    [
      engine,
      setLanguage,
      currentLanguage,
      languages,
      isReady,
      error,
      refreshTranslations,
      resourceVersion,
    ]
  );

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

/**
 * Hook para acceder al contexto de traducción
 * Implementa una política de "Fallo Amistoso" (Graceful Fallback):
 * Si no hay un TranslationProvider en la raíz, no rompe la aplicación,
 * sino que devuelve un estado seguro por defecto.
 */
export const useTranslationInternal = () => {
  const context = useContext(TranslationContext);

  if (context === undefined) {
    // Intentar recuperar idiomas del localStorage como último recurso
    const cachedLangs = languageStorage.getAvailableLanguages() || [
      { codigo: 'es', nombre: 'Español' },
      { codigo: 'en', nombre: 'English' }
    ];

    const currentLang = languageStorage.getLanguage() || 'es';

    return {
      translate: (key: string, arg?: TranslationArg) => resolveTranslation(key, key, arg),
      t: (key: string, arg?: TranslationArg) => resolveTranslation(key, key, arg),
      // Sin provider no hay motor ni catálogo: ninguna clave está registrada.
      resolve: () => null,
      setLanguage: async (langInput: string) => {
        console.warn('TranslationProvider no encontrado. Usando fallback básico (solo LocalStorage).');
        // Se normaliza igual que en el provider: sin este filtro un valor que no es código se
        // persistía en `localStorage` como "[object Object]" y sobrevivía al reload, dejando la app
        // clavada en un idioma inexistente.
        const lang = readLanguageCode(langInput);
        if (lang === null) {
          console.warn('[i18n] setLanguage recibió un idioma que no es un código válido; se ignora.', langInput);
          return;
        }
        languageStorage.setLanguage(lang);
        // Forzar un reload simple si estamos en modo fallback extremo para aplicar el cambio
        window.location.reload();
      },
      currentLanguage: currentLang,
      languages: cachedLangs,
      isReady: true,
      error: null,
      refreshTranslations: async () => {
        console.warn('TranslationProvider no encontrado. No hay catálogo que refrescar.');
      },
      resourceVersion: 0,
    } as TranslationContextType;
  }

  return context;
};
